import * as React from "react";
import { tv, VariantProps } from "tailwind-variants";
import { cn } from "@repo/utils/cn";
import { ComponentProps } from "react";
import { ChevronIcon } from "./ChevronIcon";
import { SimpleRichText, SimpleRichTextProps } from "./SimpleRichText";
import { motion } from "motion/react";

export const accordion = tv({
  base: cn(
    "uipm:w-full uipm:flex uipm:flex-col",
    "uipm:font-[Inter] uipm:font-normal",
  ),
  variants: {
    variant: {
      default: "",
      compact: "",
      inline: "",
      "location-inline": "",
    },
  },
  defaultVariants: {
    variant: "default",
  },
});

const accordionItem = tv({
  base: cn("uipm:w-full"),
  variants: {
    isOpen: {
      true: "",
      false: "",
    },
    backgroundColor: {
      none: "uipm:bg-[var(--surface-surface-primary)]",
      light: "uipm:bg-[var(--surface-surface-secondary)]",
      accent: "uipm:bg-[var(--surface-surface-accent-secondary)]",
      "accent-light": "uipm:bg-[var(--surface-surface-accent-light)]",
    },
    variant: {
      default: "uipm:rounded-[var(--radius-radius-lg)]",
      compact: "uipm:rounded-[var(--radius-radius-sm)]",
      inline: "",
      "location-inline": "",
    },
  },
  compoundVariants: [
    {
      variant: "default",
      isOpen: true,
      className:
        "uipm:py-[var(--padding-padding-sm-plus)] uipm:px-[var(--padding-padding-default)]",
    },
    {
      variant: "default",
      isOpen: false,
      className:
        "uipm:py-[var(--padding-padding-sm-plus)] uipm:px-[var(--padding-padding-default)]",
    },
    {
      variant: "compact",
      isOpen: true,
      className:
        "uipm:py-[var(--padding-padding-sm-plus)] uipm:px-[var(--padding-padding-default)] uipm:pb-[24px]",
    },
    {
      variant: "compact",
      isOpen: false,
      className:
        "uipm:py-[var(--padding-padding-sm-plus)] uipm:px-[var(--padding-padding-default)]",
    },
    {
      variant: "inline",
      isOpen: true,
      className: "",
    },
    {
      variant: "inline",
      isOpen: false,
      className: "",
    },
  ],
  defaultVariants: {
    isOpen: false,
    backgroundColor: "none",
    variant: "default",
  },
});

const accordionItemButton = tv({
  base: cn(
    "uipm:w-full uipm:flex uipm:items-center uipm:justify-between",
    "uipm:text-left uipm:bg-transparent uipm:border-0 uipm:cursor-pointer",
    "uipm:outline-none focus:uipm:outline-none",
    "uipm:hover:bg-opacity-80",
  ),
});

const accordionItemTitle = tv({
  base: cn(
    "uipm:text-[var(--typography-sizes-body-large)]",
    "uipm:leading-[var(--typography-line-heights-body-large)]",
    "uipm:font-normal uipm:w-full uipm:text-left",
  ),
  variants: {
    variant: {
      default: "",
      compact: "",
      inline: "",
      "location-inline":
        "uipm:text-[var(--typography-sizes-body-medium)] uipm:leading-[var(--typography-line-heights-body-medium)] uipm:font-medium",
    },
  },
});

const accordionItemTitleWrapper = tv({
  base: cn(
    "uipm:text-[var(--typography-colors-text-heading)]",
    "uipm:flex-1 uipm:w-full",
  ),
});

const accordionItemContent = tv({
  base: cn("uipm:pt-0"),
});

const accordionItemText = tv({
  base: cn(
    "uipm:text-[var(--typography-colors-text-faded-dark)]",
    "uipm:mt-[var(--margin-margin-default)]",
  ),
});

const chevronWrapper = tv({
  base: cn(
    "uipm:ml-4 uipm:transition-transform uipm:duration-200 uipm:ease-in-out",
  ),
  variants: {
    isOpen: {
      true: "uipm:rotate-270",
      false: "uipm:rotate-90",
    },
  },
  defaultVariants: {
    isOpen: false,
  },
});

export interface AccordionItem {
  title: string;
  text?: SimpleRichTextProps[] | null;
  backgroundColor?: "none" | "light" | "accent" | "accent-light";
}

export interface AccordionProps extends Omit<ComponentProps<"div">, "title"> {
  items: AccordionItem[];
  defaultOpenIndex?: number;
  variant?: "default" | "compact" | "inline" | "location-inline";
  onItemToggle?: (index: number, title: string, isOpen: boolean) => void;
}

export const Accordion: React.FC<AccordionProps> = ({
  items,
  defaultOpenIndex,
  variant = "default",
  className,
  onItemToggle,
  ...props
}) => {
  const [openIndex, setOpenIndex] = React.useState<number | null>(
    defaultOpenIndex ?? null,
  );

  const toggleItem = (index: number) => {
    const newIsOpen = openIndex !== index;
    setOpenIndex(openIndex === index ? null : index);
    if (onItemToggle) {
      onItemToggle(index, items[index]?.title || "", newIsOpen);
    }
  };

  if (!items || items.length === 0) return null;

  return (
    <div className={cn(accordion({ variant }), className)} {...props}>
      <div
        className={cn(
          "uipm:flex uipm:flex-col uipm:w-full",
          variant === "default"
            ? "uipm:gap-[var(--padding-padding-sm-plus)]"
            : "uipm:gap-4",
        )}
      >
        {items.map((item, index) => {
          const isOpen = openIndex === index;
          const bgColor = item.backgroundColor || "none";

          return (
            <div
              key={index}
              className={accordionItem({
                isOpen,
                backgroundColor: bgColor,
                variant,
              })}
            >
              <button
                className={accordionItemButton()}
                onClick={() => toggleItem(index)}
                aria-expanded={isOpen}
                aria-controls={`accordion-content-${index}`}
              >
                <div className={accordionItemTitleWrapper()}>
                  <h3 className={accordionItemTitle({ variant })}>
                    {item.title}
                  </h3>
                </div>
                <div className={chevronWrapper({ isOpen })}>
                  <ChevronIcon
                    className="uipm:w-6 uipm:h-6"
                    color="var(--color-color-accent)"
                  />
                </div>
              </button>

              {item.text && item.text.length > 0 && (
                <motion.div
                  initial={false}
                  animate={{
                    height: isOpen ? "auto" : 0,
                    opacity: isOpen ? 1 : 0,
                  }}
                  transition={{ duration: 0.3, ease: "easeInOut" }}
                  className={cn(
                    accordionItemContent(),
                    "uipm:overflow-hidden",
                  )}
                  id={`accordion-content-${index}`}
                  role="region"
                  aria-labelledby={`accordion-button-${index}`}
                  aria-hidden={!isOpen}
                >
                  <div
                    className={cn(
                      accordionItemText(),
                      item.text.length === 2 &&
                        "uipm:grid uipm:grid-cols-1 uipm:md:grid-cols-2 uipm:gap-[var(--padding-padding-default)]",
                    )}
                  >
                    {item.text.map((textItem, textIndex) => (
                      <div key={textIndex}>
                        <SimpleRichText {...textItem} />
                      </div>
                    ))}
                  </div>
                </motion.div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

export type AccordionVariants = VariantProps<typeof accordion>;
